import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Typography } from '@mui/material';
import { debounce } from '@mui/material/utils';
import EntityCard from 'features/IRWorkstation/components/Entity/EntityCard';
import FilterSelect from 'features/IRWorkstation/components/Common/FilterSelect';
import PinButton from 'features/IRWorkstation/components/Common/PinButton';
import SearchInput from 'features/IRWorkstation/components/Search/SearchInput';
import { SEARCH_DEBOUNCE_MS } from 'features/IRWorkstation/constants/debounce';
import useEntities from 'features/IRWorkstation/hooks/useEntities';
import useEntityDashboardPin, {
	getPinState,
	PIN_STATES,
} from 'features/IRWorkstation/hooks/useEntityDashboardPin';
import { transformEntityToCard } from 'features/IRWorkstation/utils/transformEntityToCard';
import salesforceEntitiesService from 'services/salesforceEntitiesService';
import { useIRWorkstation } from '../contexts/IRWorkstationContext';

// Track Events
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { PageLoadsTargets } from 'constants/pageLoadTargets';

const PAGE_SIZE = 25;

// "All" options are modeled as an empty value so no filter param is sent
const ALL_TYPES = { label: 'All Types', value: '' };
const ALL_STATUSES = { label: 'All Statuses', value: '' };

const PIN_BUTTON = {
	[PIN_STATES.DISABLED]: { label: 'Pin', title: 'Select a filter to pin' },
	[PIN_STATES.PIN]: {
		label: 'Pin',
		title: 'Pin filters as default',
		trackKey: 'pin',
	},
	[PIN_STATES.PINNED]: {
		label: 'Pinned',
		title: 'Unpin filters',
		trackKey: 'unpin',
	},
	[PIN_STATES.UPDATE]: {
		label: 'Update pin',
		title: 'Pin these filters instead',
		trackKey: 'pinUpdate',
	},
	[PIN_STATES.CLEAR]: {
		label: 'Clear pin',
		title: 'Clear pinned filters',
		trackKey: 'pinClear',
	},
};

const gridStyles = {
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
	gap: '12px',
};

const EntityDashboard = () => {
	const navigate = useNavigate();
	const { tenant } = useIRWorkstation();
	const { trackButtonClick, trackPageLoad } = useTrackEvent({ tenant, product: 'IEM' });

	const [searchInput, setSearchInput] = useState('');
	// the value actually sent to the API; debounced so results update as the user
	// types without a request per keystroke
	const [search, setSearch] = useState('');
	const { pin, setPin } = useEntityDashboardPin();
	const [entityType, setEntityType] = useState(pin?.entityType ?? '');
	const [status, setStatus] = useState(pin?.status ?? '');
	const [picklistValues, setPicklistValues] = useState(null);

	const pinState = getPinState(pin, { entityType, status });
	const pinButton = PIN_BUTTON[pinState];

	const handleButtonClickTrackEvent = useCallback(
		(targetKey) => {
			const targetMap = {
				typeFilter: ButtonClickTargets.EntityDashboardTypeFilter,
				statusFilter: ButtonClickTargets.EntityDashboardStatusFilter,
				search: ButtonClickTargets.EntityDashboardSearch,
				raiseDashboard: ButtonClickTargets.EntityDashboardRaiseDashboard,
				edit: ButtonClickTargets.EntityDashboardEdit,
				pin: ButtonClickTargets.EntityDashboardPin,
				unpin: ButtonClickTargets.EntityDashboardUnpin,
				pinUpdate: ButtonClickTargets.EntityDashboardPinUpdate,
				pinClear: ButtonClickTargets.EntityDashboardPinClear,
			};

			const target = targetMap[targetKey];
			if (target) {
				trackButtonClick(target);
			}
		},
		[trackButtonClick]
	);

	const debouncedSetSearch = useMemo(
		() => debounce(setSearch, SEARCH_DEBOUNCE_MS),
		[]
	);

	const debouncedSearchTracking = useMemo(
		() => debounce(() => handleButtonClickTrackEvent('search'), 1000),
		[handleButtonClickTrackEvent]
	);

	useEffect(
		() => () => {
			debouncedSetSearch.clear();
			debouncedSearchTracking.clear();
		},
		[debouncedSetSearch, debouncedSearchTracking]
	);

	const handleSearchChange = useCallback(
		(event) => {
			setSearchInput(event.target.value);
			debouncedSetSearch(event.target.value);
			debouncedSearchTracking();
		},
		[debouncedSetSearch, debouncedSearchTracking]
	);

	const handleEntityTypeChange = useCallback(
		(value) => {
			setEntityType(value);
			handleButtonClickTrackEvent('typeFilter');
		},
		[handleButtonClickTrackEvent]
	);

	const handleStatusChange = useCallback(
		(value) => {
			setStatus(value);
			handleButtonClickTrackEvent('statusFilter');
		},
		[handleButtonClickTrackEvent]
	);

	const handlePinClick = useCallback(() => {
		handleButtonClickTrackEvent(pinButton.trackKey);
		setPin(
			pinState === PIN_STATES.PINNED || pinState === PIN_STATES.CLEAR
				? null
				: { entityType, status }
		);
	}, [
		handleButtonClickTrackEvent,
		pinButton,
		pinState,
		setPin,
		entityType,
		status,
	]);

	const handleEntityCardClick = useCallback(
		(entityId) => {
			handleButtonClickTrackEvent('raiseDashboard');
			navigate(entityId);
		},
		[handleButtonClickTrackEvent, navigate]
	);

	const {
		entities,
		loading: entitiesLoading,
		loadingMore,
		error: entitiesError,
		total,
		loadMore,
		refresh,
	} = useEntities(PAGE_SIZE, { search, entityType, status });

	// Filter dropdown options come from the Salesforce picklists; a failure leaves
	// the dropdowns with only their "All" option rather than breaking the page.
	useEffect(() => {
		let cancelled = false;

		salesforceEntitiesService
			.getPicklistValues()
			.then((data) => {
				if (!cancelled) setPicklistValues(data);
			})
			.catch((err) => {
				console.error('Failed to load entity filter options:', err);
			});

		return () => {
			cancelled = true;
		};
	}, []);

	// A pinned value can disappear if the Salesforce picklist changes. Once the
	// options are known, drop anything no longer selectable so the user isn't left
	// with a blank dropdown and an unexplained empty result set.
	useEffect(() => {
		if (!picklistValues) return;

		const isValid = (options, value) =>
			!value || (options || []).some((option) => option.value === value);

		const validType = isValid(picklistValues.entity_type, entityType);
		const validStatus = isValid(picklistValues.status, status);

		if (validType && validStatus) return;

		const nextType = validType ? entityType : '';
		const nextStatus = validStatus ? status : '';

		setEntityType(nextType);
		setStatus(nextStatus);

		if (pin) setPin({ entityType: nextType, status: nextStatus });
	}, [picklistValues, entityType, status, pin, setPin]);

	const entityTypeOptions = [ALL_TYPES, ...(picklistValues?.entity_type || [])];
	const statusOptions = [ALL_STATUSES, ...(picklistValues?.status || [])];

	const entityCards = entities?.map(transformEntityToCard) || [];

	const scrollContainerRef = useRef(null);

	const handleScroll = useCallback(
		(event) => {
			const el = event.currentTarget;

			// loadMore no-ops while a load is in flight, on error, or once all records are loaded
			if (
				!entitiesError &&
				el.scrollHeight - el.scrollTop <= el.clientHeight + 50
			) {
				loadMore();
			}
		},
		[entitiesError, loadMore]
	);

	// If the loaded cards don't overflow the container, a scroll event can never
	// fire. Keep loading pages until the content overflows; loadMore no-ops once
	// all records are loaded or if an error has occurred.
	useEffect(() => {
		const el = scrollContainerRef.current;

		if (el && !entitiesError && el.scrollHeight <= el.clientHeight) {
			loadMore();
		}
	}, [entityCards.length, entitiesError, loadMore]);

	useEffect(() => {
		trackPageLoad(PageLoadsTargets.EntityDashboard);
	}, []);

	return (
		<Box
			ref={scrollContainerRef}
			onScroll={handleScroll}
			data-testid='entities-scroll-container'
			sx={{
				flex: 1,
				overflowY: 'auto',
				padding: '16px 20px',
				backgroundColor: 'background.default',
				display: 'flex',
				flexDirection: 'column',
				gap: '14px',
			}}
		>
			{/* page header */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: '12px',
				}}
			>
				<Box>
					<Typography sx={{ fontSize: '17px', fontWeight: 700 }}>
						Entity Dashboard
					</Typography>
					<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
						{total ? `${total} entities` : ''}
					</Typography>
				</Box>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
						flexShrink: 0,
					}}
				>
					<PinButton
						data-testid='entity-dashboard-pin'
						label={pinButton.label}
						title={pinButton.title}
						active={pinState === PIN_STATES.PINNED}
						disabled={pinState === PIN_STATES.DISABLED}
						onClick={handlePinClick}
					/>
					<FilterSelect
						data-testid='entity-type-filter'
						value={entityType}
						options={entityTypeOptions}
						onChange={handleEntityTypeChange}
						highlighted={
							Boolean(pin?.entityType) && entityType === pin.entityType
						}
					/>
					<FilterSelect
						data-testid='entity-status-filter'
						value={status}
						options={statusOptions}
						onChange={handleStatusChange}
						highlighted={Boolean(pin?.status) && status === pin.status}
					/>
					<SearchInput
						data-testid='entity-search-input'
						value={searchInput}
						onChange={handleSearchChange}
						placeholder='Search entities…'
					/>
				</Box>
			</Box>
			{entitiesError ? (
				<EntityCard.Error message='Failed to load entities' />
			) : (
				<Box sx={gridStyles}>
					{entitiesLoading ? (
						<>
							{Array.from({ length: PAGE_SIZE }).map((_, index) => (
								<EntityCard.Skeleton key={index} />
							))}
						</>
					) : entityCards.length === 0 ? (
						<EntityCard.Empty />
					) : (
						<>
							{entityCards.map((card) => (
								<EntityCard
									key={card.id}
									card={card}
									onClick={() => handleEntityCardClick(card.id)}
									onRefresh={refresh}
									onEditClick={() => handleButtonClickTrackEvent('edit')}
								/>
							))}
							{loadingMore &&
								Array.from({ length: 4 }).map((_, index) => (
									<EntityCard.Skeleton key={`loading-more-${index}`} />
								))}
						</>
					)}
				</Box>
			)}
		</Box>
	);
};

export default EntityDashboard;
